import { useState, useCallback } from 'react'
import StampEditor from './StampEditor'
import ExportPanel from './ExportPanel'

/* ── Section must live OUTSIDE Menu to keep a stable React identity ── */
function Section({ id, title, collapsed, onToggle, children }) {
  return (
    <div className={`menu-section ${collapsed ? 'collapsed' : ''}`}>
      <h3 onClick={() => onToggle(id)}>
        <span className="section-arrow">{collapsed ? '▸' : '▾'}</span>
        {title}
      </h3>
      {!collapsed && <div className="section-body">{children}</div>}
      <div className="section-perf-edge" />
    </div>
  )
}

const BG_PRESETS = [
  { label: 'Solid', value: 'solid' },
  { label: 'Dots', value: 'dots' },
  { label: 'Grid', value: 'grid' },
  { label: 'Grain', value: 'grain' },
]

function Menu({ open, config, updateConfig }) {
  const [collapsed, setCollapsed] = useState({})

  const toggle = useCallback((key) => setCollapsed(prev => ({ ...prev, [key]: !prev[key] })), [])

  const handleImageUpload = (layer, event) => {
    const file = event.target.files[0]
    if (file) {
      const reader = new FileReader()
      reader.onload = (e) => {
        const img = new Image()
        img.onload = () => {
          updateConfig(layer, { image: img })
        }
        img.src = e.target.result
      }
      reader.readAsDataURL(file)
    }
  }

  const loadDefaultImage = (layer, imagePath) => {
    const img = new Image()
    img.onload = () => {
      updateConfig(layer, { image: img })
    }
    img.src = imagePath
  }

  const siteBg = config.siteBg || { mode: 'solid', color: '#0e0e0e', accentColor: '#ffffff', dotSize: 1, dotSpacing: 24, grainOpacity: 0.08 }

  return (
    <div className={`menu-container ${open ? 'open' : ''}`}>
      <div className="menu-perforated-edge left" />
      <div className="menu-perforated-edge right" />

      <div className="menu-inner">
        <div className="menu-title">✦ Panorama Controls</div>

        {/* ── Site Background ── */}
        <Section id="siteBg" title="Background" collapsed={!!collapsed['siteBg']} onToggle={toggle}>
          <div className="control-group">
            <label className="control-label">Style</label>
            <div className="format-buttons">
              {BG_PRESETS.map(p => (
                <button key={p.value}
                  className={`format-btn ${siteBg.mode === p.value ? 'active' : ''}`}
                  onClick={() => updateConfig('siteBg', { ...siteBg, mode: p.value })}>
                  {p.label}
                </button>
              ))}
            </div>
          </div>
          <div className="control-row">
            <div className="control-group half">
              <label className="control-label">Color</label>
              <input type="color" value={siteBg.color}
                onChange={(e) => updateConfig('siteBg', { ...siteBg, color: e.target.value })} />
            </div>
            <div className="control-group half">
              <label className="control-label">Accent</label>
              <input type="color" value={siteBg.accentColor || '#ffffff'}
                onChange={(e) => updateConfig('siteBg', { ...siteBg, accentColor: e.target.value })} />
            </div>
          </div>
          {(siteBg.mode === 'dots' || siteBg.mode === 'grid') && (
            <div className="control-row">
              <div className="control-group half">
                <label className="control-label">Size</label>
                <input type="range" min="0.5" max="4" step="0.1"
                  value={siteBg.dotSize || 1}
                  onChange={(e) => updateConfig('siteBg', { ...siteBg, dotSize: parseFloat(e.target.value) })} />
                <span className="value-label">{(siteBg.dotSize || 1).toFixed(1)}</span>
              </div>
              <div className="control-group half">
                <label className="control-label">Spacing</label>
                <input type="range" min="8" max="60" step="1"
                  value={siteBg.dotSpacing || 24}
                  onChange={(e) => updateConfig('siteBg', { ...siteBg, dotSpacing: parseFloat(e.target.value) })} />
                <span className="value-label">{siteBg.dotSpacing || 24}px</span>
              </div>
            </div>
          )}
          {siteBg.mode === 'grain' && (
            <div className="control-group">
              <label className="control-label">Intensity</label>
              <input type="range" min="0" max="0.3" step="0.01"
                value={siteBg.grainOpacity || 0.08}
                onChange={(e) => updateConfig('siteBg', { ...siteBg, grainOpacity: parseFloat(e.target.value) })} />
              <span className="value-label">{((siteBg.grainOpacity || 0.08) * 100).toFixed(0)}%</span>
            </div>
          )}
        </Section>

        {/* ── Window ── */}
        <Section id="window" title="Window" collapsed={!!collapsed['window']} onToggle={toggle}>
          <div className="control-group">
            <label className="control-label">Width</label>
            <input type="range" min="30" max="100" value={config.window.width}
              onChange={(e) => updateConfig('window', { width: parseFloat(e.target.value) })} />
            <span className="value-label">{config.window.width}vw</span>
          </div>
          <div className="control-group">
            <label className="control-label">Height</label>
            <input type="range" min="15" max="80" value={config.window.height}
              onChange={(e) => updateConfig('window', { height: parseFloat(e.target.value) })} />
            <span className="value-label">{config.window.height}vw</span>
          </div>
          <div className="control-group">
            <label className="control-label">Corner Radius</label>
            <input type="range" min="0" max="80" value={config.window.borderRadius}
              onChange={(e) => updateConfig('window', { borderRadius: parseFloat(e.target.value) })} />
            <span className="value-label">{config.window.borderRadius}px</span>
          </div>
        </Section>

        {/* ── Position Mode ── */}
        <Section id="position" title="Position Mode" collapsed={!!collapsed['position']} onToggle={toggle}>
          <div className="control-group">
            <label className="control-label">
              <input type="checkbox" checked={config.paused}
                onChange={(e) => updateConfig('root', { paused: e.target.checked })} />
              Pause Animation
            </label>
          </div>
          {config.paused && (
            <>
              <div className="control-group">
                <label className="control-label">Background Offset</label>
                <input type="range" min="0" max="5000"
                  value={config.background.offset}
                  onChange={(e) => updateConfig('background', { offset: parseFloat(e.target.value) })} />
                <span className="value-label">{config.background.offset.toFixed(0)}px</span>
              </div>
              <div className="control-group">
                <label className="control-label">Foreground Offset</label>
                <input type="range" min="0" max="5000"
                  value={config.foreground.offset}
                  onChange={(e) => updateConfig('foreground', { offset: parseFloat(e.target.value) })} />
                <span className="value-label">{config.foreground.offset.toFixed(0)}px</span>
              </div>
            </>
          )}
        </Section>

        {/* ── Background Layer ── */}
        <Section id="background" title="Background Layer" collapsed={!!collapsed['background']} onToggle={toggle}>
          <div className="control-group">
            <label className="control-label">Speed</label>
            <input type="range" min="0" max="20" step="0.1"
              value={config.background.speed}
              onChange={(e) => updateConfig('background', { speed: parseFloat(e.target.value) })} />
            <span className="value-label">{config.background.speed.toFixed(1)}</span>
          </div>
          <div className="control-group">
            <label className="control-label">Direction</label>
            <select value={config.background.direction}
              onChange={(e) => updateConfig('background', { direction: parseInt(e.target.value) })}>
              <option value={1}>→ Right</option>
              <option value={-1}>← Left</option>
            </select>
          </div>
          <div className="control-group">
            <label className="control-label">Motion Blur</label>
            <input type="range" min="0" max="30" step="1"
              value={config.background.motionBlur}
              onChange={(e) => updateConfig('background', { motionBlur: parseFloat(e.target.value) })} />
            <span className="value-label">{config.background.motionBlur}px</span>
          </div>
          <div className="control-group">
            <label className="control-label">Image</label>
            <input type="file" accept="image/*" onChange={(e) => handleImageUpload('background', e)} />
            <button className="secondary"
              onClick={() => loadDefaultImage('background', '/image/Panorama ciel.jpg')}>
              Load Default
            </button>
          </div>
        </Section>

        {/* ── Foreground Layer ── */}
        <Section id="foreground" title="Foreground Layer" collapsed={!!collapsed['foreground']} onToggle={toggle}>
          <div className="control-group">
            <label className="control-label">Speed</label>
            <input type="range" min="0" max="20" step="0.1"
              value={config.foreground.speed}
              onChange={(e) => updateConfig('foreground', { speed: parseFloat(e.target.value) })} />
            <span className="value-label">{config.foreground.speed.toFixed(1)}</span>
          </div>
          <div className="control-group">
            <label className="control-label">Direction</label>
            <select value={config.foreground.direction}
              onChange={(e) => updateConfig('foreground', { direction: parseInt(e.target.value) })}>
              <option value={1}>→ Right</option>
              <option value={-1}>← Left</option>
            </select>
          </div>
          <div className="control-group">
            <label className="control-label">Motion Blur</label>
            <input type="range" min="0" max="30" step="1"
              value={config.foreground.motionBlur}
              onChange={(e) => updateConfig('foreground', { motionBlur: parseFloat(e.target.value) })} />
            <span className="value-label">{config.foreground.motionBlur}px</span>
          </div>
          <div className="control-group">
            <label className="control-label">Image</label>
            <input type="file" accept="image/*" onChange={(e) => handleImageUpload('foreground', e)} />
            <button className="secondary"
              onClick={() => loadDefaultImage('foreground', '/image/first-plan-transparent.png')}>
              Load Default
            </button>
          </div>
        </Section>

        {/* ── Stamps ── */}
        <StampEditor
          stamps={config.stamps}
          updateStamps={(stamps) => updateConfig('stamps', stamps)}
        />

        {/* ── Export ── */}
        <ExportPanel
          fullConfig={config}
          config={config.export}
          updateConfig={(updates) => updateConfig('export', updates)}
        />
      </div>
    </div>
  )
}

export default Menu
